/**
 * GbBaseStripeService
 * Core architectural base class handling Stripe initialization, execution, and confirmation pipelines.
 */
import { Inject, Injectable } from '@angular/core'
import {
  PaymentIntent,
  PaymentIntentResult,
  PaymentMethod,
  SetupIntent,
  SetupIntentResult,
  Stripe,
  StripeConstructorOptions,
  StripeElements,
  StripeExpressCheckoutElement,
  StripeExpressCheckoutElementConfirmEvent,
  StripePaymentElement,
  loadStripe,
} from '@stripe/stripe-js'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { PaymentEntity, StripePaymentIntentEntity } from 'libs/shared/models/payment/payment.models'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import { Subject } from 'rxjs'

export type StripePaymentPayload = Omit<PaymentEntity, 'profile' | 'volunteer'> | StripePaymentIntentEntity

@Injectable()
export abstract class GbBaseStripeService {
  protected _stripe: Stripe | null = null
  protected _elements?: StripeElements
  protected _paymentElement?: StripePaymentElement
  protected _expressCheckoutElement?: StripeExpressCheckoutElement
  protected _setupIntent: SetupIntent | null = null
  private _payment: StripePaymentPayload | null = null
  protected paymentSubject$ = new Subject<StripePaymentPayload | null>()

  public set stripe(stripe: Stripe | null) {
    this._stripe = stripe
  }

  public get stripe(): Stripe | null {
    return this._stripe
  }

  public set stripeElements(elements: StripeElements | undefined) {
    this._elements = elements
  }

  public get stripeElements(): StripeElements | undefined {
    return this._elements
  }

  public set setupIntent(setupIntent: SetupIntent | null) {
    this._setupIntent = setupIntent
  }

  public get setupIntent(): SetupIntent | null {
    return this._setupIntent
  }

  public get paymentIntent(): PaymentIntent | undefined {
    if (!this._payment) return
    if ('stripe_payment_intent' in this._payment) {
      return this._payment.stripe_payment_intent?.payment_intent
    }
    return this._payment.payment_intent
  }

  public set payment(payment: StripePaymentPayload | null) {
    this._payment = payment
    this.paymentSubject$.next(payment)
  }

  public get payment(): StripePaymentPayload | null {
    return this._payment
  }

  public get payment$() {
    return this.paymentSubject$.asObservable()
  }

  constructor(
    @Inject(APP_CONFIG) protected appConfig: { stripe_publishable_key: string },
    protected gbNotificationService: GbNotificationService,
  ) {}

  async initStripe(stripeUserId: string, isExpress: boolean): Promise<Stripe | null> {
    const stripeOptions: StripeConstructorOptions = {}

    if (!isExpress && stripeUserId) {
      stripeOptions.stripeAccount = stripeUserId
    }

    return loadStripe(this.appConfig.stripe_publishable_key, stripeOptions).then((stripe) => {
      if (stripe) this.stripe = stripe
      return stripe
    })
  }

  async confirmCardSetup(): Promise<SetupIntentResult> {
    if (!this.stripe || !this.setupIntent || !this.setupIntent.client_secret || !this.stripeElements) {
      throw new Error('Stripe runtime dependencies uninitialized.')
    }

    await this.stripeElements.submit()

    return this.stripe.confirmSetup({
      elements: this.stripeElements,
      clientSecret: this.setupIntent.client_secret,
      redirect: 'if_required',
    })
  }

  /**
   * Safe Async/Await Payment Execution Pipeline
   * Completely mitigates the original infinite loop bug while supporting optional parameters.
   */
  async confirmPayment(
    paymentMethodId: string | null | undefined,
    ev?: StripeExpressCheckoutElementConfirmEvent,
    isPreAuth?: boolean,
  ): Promise<{ success: boolean }> {
    const stripe = this.stripe
    const clientSecret = this.paymentIntent?.client_secret

    if (!clientSecret || !stripe || !this.stripeElements) {
      this.onPaymentError()
      console.error('Missing runtime parameters:', {
        clientSecret: !!clientSecret,
        stripe: !!stripe,
        elements: !!this.stripeElements,
      })
      return { success: false }
    }

    try {
      let confirmResult: PaymentIntentResult

      if (paymentMethodId) {
        confirmResult = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: paymentMethodId },
          { handleActions: true },
        )
      } else {
        if (!ev) await this.stripeElements.submit()

        confirmResult = await stripe.confirmPayment({
          elements: this.stripeElements,
          clientSecret,
          redirect: 'if_required',
          confirmParams: { return_url: window.location.href },
        })
      }

      if (confirmResult.error) {
        this.onPaymentError(confirmResult.error.message)
        return { success: false }
      }

      // Linear loop-prevention block for 3D Secure verification challenges
      if (confirmResult.paymentIntent?.status === 'requires_action') {
        const nextActionResult = await stripe.handleNextAction({ clientSecret })

        if (nextActionResult.error) {
          this.onPaymentError(nextActionResult.error.message)
          return { success: false }
        }

        this.onPaymentSuccessHook(nextActionResult.paymentIntent?.payment_method, ev, isPreAuth)
        return { success: true }
      }

      this.onPaymentSuccessHook(confirmResult.paymentIntent?.payment_method, ev, isPreAuth)
      return { success: true }
    } catch (err) {
      this.onPaymentError()
      console.error('Stripe native transaction crash:', err)
      return { success: false }
    }
  }

  /**
   * Contract hook implemented by sub-features to bridge to unique Store architectures.
   *
   * `ev` and `isPreAuth` are optional by design — features without digital-wallet
   * or pre-authorization flows may omit them from their override signature entirely.
   * If your feature *does* support either flow, make sure your override actually
   * declares and forwards these params; TypeScript will not warn you if you silently
   * drop them, since narrower overrides are structurally assignable to this signature.
   */
  protected abstract onPaymentSuccessHook(
    paymentMethod: string | PaymentMethod | null | undefined,
    ev?: StripeExpressCheckoutElementConfirmEvent,
    isPreAuth?: boolean,
  ): void

  onPaymentError(errorMessage = 'PAYMENT.ERROR'): void {
    this.gbNotificationService.error(errorMessage)
  }

  async validatePaymentElement(): Promise<boolean> {
    if (!this.stripeElements) return false
    const { error } = await this.stripeElements.submit()
    return !error
  }

  protected extractPaymentMethodId(paymentMethod: string | PaymentMethod | null | undefined): string | undefined {
    return typeof paymentMethod === 'string' ? paymentMethod : paymentMethod?.id
  }
}
